import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../navbar/navbar-desktop.tsx";
import Footer from "../footer/footer.tsx";
import { FaCheck, FaPhoneAlt } from "react-icons/fa";

/* Web3Forms – form "Wellness", poptávky chodí na wellness@hospudkapodrablinu.cz */
const ACCESS_KEY = "552d0844-4af9-42a9-b2b3-e3c858a01c39";

const CASY = ["14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];

type FormState = {
  jmeno: string;
  prijmeni: string;
  email: string;
  telefon: string;
  termin: string;
  cas: string;
  pocet_osob: string;
  ubytovani: string;
  poznamka: string;
  gdpr: boolean;
  botcheck: string;
};

const PRAZDNY_FORM: FormState = {
  jmeno: "",
  prijmeni: "",
  email: "",
  telefon: "",
  termin: "",
  cas: "",
  pocet_osob: "",
  ubytovani: "Nemám zájem",
  poznamka: "",
  gdpr: false,
  botcheck: "",
};

const RezervaceWellness: React.FC = () => {
  const [form, setForm] = useState<FormState>(PRAZDNY_FORM);
  const [odeslano, setOdeslano] = useState(false);
  const [chyba, setChyba] = useState("");
  const [loading, setLoading] = useState(false);

  /* Dnešek jako minimum pro výběr termínu */
  const dnes = useMemo(() => new Date().toISOString().split("T")[0], []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? target.checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setChyba("");

    if (
      !form.jmeno ||
      !form.prijmeni ||
      !form.email ||
      !form.telefon ||
      !form.termin ||
      !form.pocet_osob ||
      !form.gdpr
    ) {
      setChyba("Vyplňte prosím všechna povinná pole a potvrďte souhlas se zpracováním údajů.");
      return;
    }

    // honeypot – pokud je vyplněn, neodesíláme
    if (form.botcheck) return;

    try {
      setLoading(true);

      const payload = {
        access_key: ACCESS_KEY,
        subject: "Rezervace wellness – webový formulář",
        from_name: "Hospůdka pod Rablinů – web",
        reply_to: form.email,
        email: form.email,
        jmeno: form.jmeno,
        prijmeni: form.prijmeni,
        telefon: form.telefon,
        termin: form.termin,
        cas_prichodu: form.cas || "Neuvedeno",
        pocet_osob: form.pocet_osob,
        ubytovani: form.ubytovani,
        poznamka: form.poznamka,
        gdpr: form.gdpr ? "Souhlas udělen" : "Ne",
        botcheck: form.botcheck,
      };

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        setOdeslano(true);
        setForm(PRAZDNY_FORM);
      } else {
        setChyba(data.message || "Odeslání se nezdařilo. Zkuste to prosím později.");
      }
    } catch {
      setChyba("Odeslání se nezdařilo. Zkuste to prosím později.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-[#e0cbaa] bg-[#fffdf9] px-4 py-3 text-sm lg:text-base text-[#3d2a1c] placeholder:text-[#a89078] outline-none transition-colors focus:border-[#60260F] focus:bg-white focus:ring-4 focus:ring-[#60260F]/10";
  const labelClass = "block text-sm font-semibold text-[#60260F] mb-2";

  return (
    <>
      {/* ================= NAVBAR – TOTÁLNÍ IZOLACE ================= */}
      <div className="relative z-50 isolate">
        <Navbar />
      </div>

      <main className="bg-[#F5E7CC] text-[#531d08] overflow-x-hidden">

        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="px-5 pt-14 pb-10 lg:pt-20 lg:pb-14 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[11px] lg:text-xs uppercase tracking-[0.28em] text-[#8c5a3c]">
              Nezávazná poptávka
            </p>
            <h1 className="mt-4 text-3xl lg:text-5xl font-bold font-serif !leading-[1.35]">
              Rezervace wellness
            </h1>
            <div className="w-12 h-px bg-[#c9a878] mx-auto mt-6" />
            <p className="mt-6 text-sm lg:text-lg leading-relaxed">
              Napište nám termín a počet osob. Ozveme se vám zpět s potvrzením
              dostupnosti a domluvíme teplotu vody i čas rozehřátí sauny.
            </p>
          </div>
        </section>

        {/* =====================================================
            FORMULÁŘ + TELEFONICKÝ KONTAKT POD NÍM
        ===================================================== */}
        <section className="px-5 lg:px-10 py-12 lg:py-20">
          <div className="max-w-5xl mx-auto space-y-6 lg:space-y-8">

            {/* ---------- FORMULÁŘ / PODĚKOVÁNÍ ---------- */}
            <div className="bg-white rounded-3xl border border-[#e8d6b8] shadow-[0_20px_50px_-30px_rgba(96,38,15,0.55)] p-6 lg:p-12">
              {odeslano ? (
                <div className="text-center py-8 lg:py-14">
                  <span className="mx-auto mb-6 w-16 h-16 rounded-2xl bg-[#f8efe1] text-[#60260F] flex items-center justify-center text-3xl">
                    <FaCheck />
                  </span>
                  <h2 className="text-2xl lg:text-3xl font-serif font-bold !leading-[1.35]">
                    Děkujeme, poptávku máme
                  </h2>
                  <p className="mt-4 text-sm lg:text-base leading-relaxed text-[#6b4530] max-w-md mx-auto">
                    Ozveme se vám zpravidla do 24 hodin. Pokud spěcháte, zavolejte
                    rovnou na <b>+420 608 608 101</b>.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/wellness" className="w-full sm:w-auto">
                      <button className="w-full border border-[#60260F] text-[#60260F] hover:bg-[#60260F] hover:text-white transition-colors px-8 py-3.5 rounded-xl text-base font-semibold">
                        Zpět na wellness
                      </button>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setOdeslano(false)}
                      className="w-full sm:w-auto bg-[#60260F] hover:bg-[#965126] transition-colors text-white px-8 py-3.5 rounded-xl text-base font-semibold"
                    >
                      Poslat další poptávku
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                  {/* KONTAKTNÍ ÚDAJE */}
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8c5a3c]">
                    Kontaktní údaje
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                    <div>
                      <label className={labelClass} htmlFor="wellness-jmeno">Jméno *</label>
                      <input id="wellness-jmeno" name="jmeno" type="text" placeholder="Např. Jan"
                        value={form.jmeno} onChange={handleChange} className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="wellness-prijmeni">Příjmení *</label>
                      <input id="wellness-prijmeni" name="prijmeni" type="text" placeholder="Např. Novák"
                        value={form.prijmeni} onChange={handleChange} className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="wellness-email">E-mail *</label>
                      <input id="wellness-email" name="email" type="email" placeholder="vas@email.cz"
                        value={form.email} onChange={handleChange} className={inputClass} required />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="wellness-telefon">Telefon *</label>
                      <input id="wellness-telefon" name="telefon" type="tel" placeholder="+420 123 456 789"
                        value={form.telefon} onChange={handleChange} className={inputClass} required />
                    </div>
                  </div>

                  <div className="h-px bg-[#efe3cf] my-8" />

                  {/* TERMÍN */}
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8c5a3c]">
                    Termín návštěvy
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
                    <div>
                      <label className={labelClass} htmlFor="wellness-termin">Datum *</label>
                      <input id="wellness-termin" name="termin" type="date" min={dnes}
                        value={form.termin} onChange={handleChange}
                        className={`${inputClass} min-h-[50px]`} required />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="wellness-cas">Čas příchodu</label>
                      <select id="wellness-cas" name="cas" value={form.cas} onChange={handleChange}
                        className={`${inputClass} min-h-[50px]`}>
                        <option value="">Domluvíme se</option>
                        {CASY.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="wellness-pocet">Počet osob *</label>
                      <input id="wellness-pocet" name="pocet_osob" type="number" min={1} max={20}
                        placeholder="Např. 4" value={form.pocet_osob} onChange={handleChange}
                        className={`${inputClass} min-h-[50px]`} required />
                    </div>
                  </div>

                  <div className="h-px bg-[#efe3cf] my-8" />

                  {/* DOPLŇUJÍCÍ INFORMACE */}
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8c5a3c]">
                    Doplňující informace
                  </p>

                  <div className="mt-4 sm:max-w-md">
                    <label className={labelClass} htmlFor="wellness-ubytovani">
                      Chcete připojit ubytování?
                    </label>
                    <select id="wellness-ubytovani" name="ubytovani" value={form.ubytovani}
                      onChange={handleChange} className={`${inputClass} min-h-[50px]`}>
                      <option value="Nemám zájem">Ne, jen wellness</option>
                      <option value="Ano, mám zájem">Ano, mám zájem o nocleh</option>
                      <option value="Rád/a se poradím">Rád/a se nejdřív poradím</option>
                    </select>
                  </div>

                  <div className="mt-5">
                    <label className={labelClass} htmlFor="wellness-poznamka">Poznámka</label>
                    <textarea
                      id="wellness-poznamka"
                      name="poznamka"
                      rows={4}
                      placeholder="Např. oslava narozenin, požadovaná teplota vody, vlastní občerstvení…"
                      value={form.poznamka}
                      onChange={handleChange}
                      className={`${inputClass} resize-y min-h-[120px]`}
                    />
                  </div>

                  {/* honeypot */}
                  <input
                    type="text"
                    name="botcheck"
                    value={form.botcheck}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ display: "none" }}
                  />

                  <label className="mt-6 flex items-start gap-3 rounded-2xl bg-[#f8efe1] border border-[#e8d6b8] px-4 py-4 cursor-pointer">
                    <input
                      type="checkbox"
                      name="gdpr"
                      checked={form.gdpr}
                      onChange={handleChange}
                      className="mt-0.5 w-[18px] h-[18px] shrink-0 accent-[#60260F]"
                      required
                    />
                    <span className="text-sm leading-relaxed text-[#4f2b1c]">
                      Souhlasím se zpracováním osobních údajů za účelem vyřízení této
                      nezávazné poptávky.
                    </span>
                  </label>

                  {chyba && (
                    <div
                      role="alert"
                      className="mt-5 rounded-2xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3.5 text-sm font-semibold text-[#b42318]"
                    >
                      {chyba}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-6 block w-full sm:w-auto sm:min-w-[340px] sm:mx-auto bg-[#60260F] hover:bg-[#965126] disabled:opacity-60 disabled:hover:bg-[#60260F] transition-colors text-white px-10 py-4 rounded-xl text-base lg:text-lg font-semibold"
                  >
                    {loading ? "Odesílám…" : "Odeslat nezávaznou poptávku"}
                  </button>

                  <p className="mt-4 text-center text-xs text-[#8c5a3c]">
                    Poptávka je nezávazná. Termín platí až po našem potvrzení.
                  </p>
                </form>
              )}
            </div>

            {/* ---------- TELEFONICKÝ KONTAKT ---------- */}
            <aside className="rounded-3xl bg-[#60260F] text-white p-6 lg:p-10 shadow-[0_25px_50px_-30px_rgba(96,38,15,0.7)] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-10">
              <div>
                <h2 className="font-serif text-xl lg:text-2xl font-bold !leading-[1.35]">
                  Raději telefonicky?
                </h2>
                <p className="mt-3 text-sm lg:text-base leading-relaxed text-[#f5e6c8] lg:max-w-xl">
                  Volné termíny vám řekneme hned. Pátky, soboty a svátky mizí
                  nejrychleji.
                </p>
              </div>
              <a href="tel:+420608608101" className="w-full lg:w-auto lg:shrink-0">
                <button className="w-full bg-[#f2c17a] hover:bg-[#e5ad5f] transition-colors text-[#3b1f0f] px-8 py-3.5 rounded-xl text-base lg:text-lg font-semibold flex items-center justify-center gap-3 whitespace-nowrap">
                  <FaPhoneAlt className="text-sm" />
                  +420 608 608 101
                </button>
              </a>
            </aside>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default RezervaceWellness;
